// Shared utilities for authentication logic
import { validateAwsConfig } from '@qse/config'
import { User, UserRoleEnum } from '@qse/constants'

import { authService } from './auth-service'

// Error types for different authentication operations
export type AuthErrorType = 'login' | 'register' | 'logout' | 'confirm' | 'general'

// AWS Configuration error message
export const AWS_CONFIG_ERROR_MESSAGE =
  'AWS configuration is incomplete. Please check your environment variables.'

// Authentication operation types
export type AuthOperation = 'login' | 'logout' | 'register' | 'confirm'

// Get redirect path based on current domain and operation type
export const getRedirectPath = (operation: AuthOperation = 'login'): string => {
  const hostname = window.location.hostname

  // For logout, register, and confirm operations, always redirect to login
  if (operation === 'logout' || operation === 'register' || operation === 'confirm') {
    return '/auth/login'
  }

  // For login operation, redirect based on domain
  if (hostname.includes('admin') || hostname.includes('admin.localhost')) {
    return '/dashboard/management/reseller'
  }

  if (hostname.includes('drive-management') || hostname.includes('drive-management.localhost')) {
    return '/storage'
  }

  // Default fallback
  return ''
}

// Create user object from Cognito attributes
export const createUserFromAttributes = (
  userId: string,
  attributes: Record<string, string>,
  email?: string,
): User => {
  // Handle admin-created users (users from AWS Dashboard)
  const isAdminCreated = attributes['custom:created_by_admin'] === 'true'
  const hasCustomAttributes =
    attributes['custom:firstName'] ||
    attributes['custom:lastName'] ||
    attributes['custom:companyName']

  // For admin-created users without custom attributes, use default values
  const firstName =
    attributes['custom:firstName'] ||
    attributes.given_name ||
    attributes.name ||
    (isAdminCreated ? 'Admin' : '')
  const lastName =
    attributes['custom:lastName'] || attributes.family_name || (isAdminCreated ? 'User' : '')
  const role = attributes['custom:role'] || (isAdminCreated ? 'user' : 'user')

  return {
    id: userId,
    email: attributes.email || email || '',
    firstName,
    lastName,
    role,
    isVerified: attributes.email_verified === 'true' || isAdminCreated || !hasCustomAttributes,
    createdAt: attributes.created_at || new Date().toISOString(),
    updatedAt: attributes.updated_at || new Date().toISOString(),
    storageLimit: 0,
    storageUsed: 0,
    usagePercent: 0,
    status: 'active',
    lastActive: '',
    actions: [],
    team: '',
    avatar: '',
    userRole: role as UserRoleEnum,
    tenant: {
      id: attributes['custom:tenantId'] || '',
      name: attributes['custom:tenantName'] || '',
      status: 'active' as const,
    },
  }
}

// Handle authentication errors consistently
export const handleAuthError = (
  error: any,
  operation: AuthErrorType,
  setStatus?: (status: string) => void,
): void => {
  console.error(`${operation} error:`, error)
  const errorMessage = authService.getUserFriendlyError(error, operation)

  setStatus?.(errorMessage)
}

// Validate AWS configuration
export const validateAwsConfiguration = (): boolean => {
  try {
    return validateAwsConfig()
  } catch (error) {
    console.error('Failed to validate AWS config:', error)

    return false
  }
}
